import { Edit01 } from "@untitledui/icons";
import { useState } from "react";
import { useAdminUsers } from "@/api/admin/users";
import { usePagination } from "@/api/pagination";
import { LogoStack } from "@/components/admin/logo-stack";
import { EffectiveRoleLabel } from "@/components/admin/role-label";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import { Avatar } from "@/components/base/avatar/avatar";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { useCache } from "@/hooks/use-cache";
import { useSort } from "@/hooks/use-sort";
import { DeleteUserModal } from "@/pages/admin/modals/delete-user";
import { EditUserModal } from "@/pages/admin/modals/edit-user";
import { formatDate, toDateOrNull } from "@/utils/date-conversion";
import { getUserInitials } from "@/utils/user-initials";

type User = NonNullable<ReturnType<typeof useAdminUsers>["data"]>["users"][number];

function formatRelativeTime(date: Date | null): string {
  if (!date) return "—";
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

export const AdminUsersTable = ({ search }: { search: string }) => {
  const pagination = usePagination();
  const sort = useSort<"firstName" | "role" | "createdAt">("createdAt");
  const { data } = useAdminUsers(pagination.pagination, sort.sort, { text: search });
  const total = useCache(data?.total);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);

  return (
    <TableCard.Root className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Table
          aria-label="Team members"
          sortDescriptor={sort.descriptor}
          onSortChange={sort.setFromDescriptor}
          className="table-fixed min-w-0"
        >
          <Table.Header>
            <Table.Head
              id="firstName"
              label="Name"
              isRowHeader
              allowsSorting
              className="w-[24%] min-w-[200px] max-w-[320px] pl-4 pr-5 py-3"
            />
            <Table.Head
              id="role"
              label="Role"
              allowsSorting
              className="w-[17%] min-w-[140px] max-w-[180px] px-2 py-3"
            />
            <Table.Head id="workspaces" label="Workspaces" className="w-[12%] min-w-[70px] max-w-[160px] px-2 py-3" />
            <Table.Head
              id="individualBrands"
              label="Individual merchants"
              className="w-[16%] min-w-[80px] max-w-[170px] px-2 py-3"
            />
            <Table.Head id="status" label="Status" className="w-[19%] min-w-[170px] max-w-[240px] px-5 py-3" />
            <Table.Head id="actions" label="" className="w-[12%] min-w-[90px] max-w-[110px] px-0 py-0" />
          </Table.Header>

          <Table.Body items={data?.users ?? []}>
            {(item) => (
              <Table.Row id={item.id} className="cursor-pointer" onAction={() => setSelectedUser(item)}>
                <Table.Cell className="pl-4 pr-6 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar
                      size="sm"
                      initials={getUserInitials(item.firstName, item.lastName)}
                      alt={`${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || item.email}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-primary truncate">
                        {item.firstName} {item.lastName}
                      </p>
                      <p className="text-sm text-tertiary truncate">{item.email}</p>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-2">
                  <EffectiveRoleLabel effectiveRole={item.effectiveRole} />
                </Table.Cell>
                <Table.Cell className="px-2">
                  <div className="@container">
                    <div className="hidden @[100px]:block">
                      <LogoStack items={item.workspaces ?? []} />
                    </div>
                    <div className="@[100px]:hidden">
                      <LogoStack items={item.workspaces ?? []} max={0} />
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="px-2">
                  <div className="@container">
                    <div className="hidden @[100px]:block">
                      <LogoStack items={item.merchants ?? []} />
                    </div>
                    <div className="@[100px]:hidden">
                      <LogoStack items={item.merchants ?? []} max={0} />
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {item.role === "paused" ? (
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-medium text-tertiary">Paused</span>
                      <span className="text-xs text-tertiary truncate">
                        {item.lastActivity
                          ? `Last active ${formatRelativeTime(toDateOrNull(item.lastActivity))}`
                          : formatDate(item.createdAt)}
                      </span>
                    </div>
                  ) : item.tosDate || item.lastActivity ? (
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-medium text-brand-tertiary_alt">Active</span>
                      <span className="text-xs text-tertiary truncate">
                        {item.lastActivity
                          ? `Last active ${formatRelativeTime(toDateOrNull(item.lastActivity))}`
                          : formatDate(item.createdAt)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-medium text-utility-blue-light-500">Invite sent</span>
                      <span className="text-xs text-tertiary truncate">{formatDate(item.createdAt)}</span>
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap px-4 py-4" onClick={(e) => e.stopPropagation()}>
                  <DeleteUserModal user={item} />
                  <ButtonUtility
                    size="xs"
                    color="tertiary"
                    tooltip="Edit"
                    icon={Edit01}
                    onClick={() => setSelectedUser(item)}
                  />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
      <PaginationPageMinimalCenter
        page={pagination.page}
        total={Math.ceil((total ?? 0) / pagination.pagination.limit)}
        onPageChange={pagination.setPage}
        className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
      />
      {/* @ts-expect-error: API dates are strings, modals expect Date */}
      <EditUserModal user={selectedUser} onClose={() => setSelectedUser(undefined)} />
    </TableCard.Root>
  );
};
